import { Component, forwardRef, Input, OnChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { DecimalPipe } from '@angular/common';

export interface MonthPickerDate {
  month: number;
  year: number;
}

@Component({
  selector: 'wsb-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthPickerComponent),
      multi: true
    }
  ],
  standalone: false
})
export class MonthPickerComponent implements ControlValueAccessor, OnChanges {
  date: MonthPickerDate;
  dateTxt: string;
  separator: string;
  monthFirst: boolean;
  place: number;

  isYearSelection = false;
  incr = 0;

  months: string[] = [
    'stats_chart_jan',
    'stats_chart_feb',
    'stats_chart_mar',
    'stats_chart_apr',
    'stats_chart_may',
    'stats_chart_jun',
    'stats_chart_jul',
    'stats_chart_aug',
    'stats_chart_sep',
    'stats_chart_oct',
    'stats_chart_nov',
    'stats_chart_dec'
  ];

  @Input() disabled = false;
  @Input() mask = 'mm/yyyy';
  @Input() minDate?: MonthPickerDate;

  @ViewChild('calendarPanel') calendar: NgbDropdown;

  private onChange: (data: MonthPickerDate) => void;
  private onTouched: () => void;

  constructor(private decimalPipe: DecimalPipe) {
    this.separator = this.mask.replace(/m|y|M/gi, '');
    this.monthFirst = this.mask.indexOf('y') > 0;
    this.place = this.mask.indexOf(this.separator);

    this.onChange = (data: MonthPickerDate) => {
      this.date = data;
      const monthStr = this.decimalPipe.transform(data.month, '2.0-0');
      this.dateTxt = this.monthFirst ? '' + monthStr + this.separator + data.year : '' + data.year + this.separator + monthStr;
      this.incr = this.getIncr(this.date.year);
    };

    this.onTouched = () => {};
  }

  ngOnChanges(): void {
    if (this.date && !this.isSelectedDateGreaterOrEqualThanMinDate(this.date.month)) {
      this.dateTxt = this.formatData(this.minDate);
      this.writeValue(Object.assign({}, this.minDate));
    }
  }

  selectYearMonth($event, index: number) {
    if (!this.isSelectedDateGreaterOrEqualThanMinDate(index + 1)) {
      return;
    }

    if (this.isYearSelection) {
      $event.stopPropagation();
      this.date.year = index + this.incr;
      this.dateTxt = this.formatData(this.date);
      this.isYearSelection = false;
      this.incr = this.getIncr(this.date.year);
    } else {
      this.date.month = index + 1;
      this.dateTxt = this.formatData(this.date);
      this.calendar.close();
    }
    this.writeValue(this.date);
  }

  showYear($event: any, show: boolean) {
    $event.stopPropagation();
    this.isYearSelection = !this.isYearSelection;
  }

  addYear($event: any, incr: number) {
    $event.stopPropagation();
    const year = this.isYearSelection ? this.date.year + 10 * incr : this.date.year + incr;
    this.date.year = year;
    this.incr = this.getIncr(year);
    this.dateTxt = this.formatData(this.date);
  }

  getIncr(year: number): number {
    return year - (year % 10) - 1;
  }

  formatData(data: MonthPickerDate): string {
    const monthTxt = data.month < 10 ? '0' + data.month : '' + data.month;
    return this.monthFirst ? monthTxt + this.separator + data.year : '' + data.year + this.separator + monthTxt;
  }

  writeValue(data: MonthPickerDate): void {
    this.date = data;
    this.onTouched();
    this.onChange(this.date);
  }

  registerOnChange(fn: (data: MonthPickerDate) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  isSelectedDateGreaterOrEqualThanMinDate(selectedYearOrMonth: number): boolean {
    if (!this.minDate) {
      return true;
    }

    if (this.isYearSelection) {
      return this.incr + selectedYearOrMonth - 1 >= this.minDate.year;
    }

    if (this.date.year > this.minDate.year) {
      return true;
    }
    return this.date.year === this.minDate.year && selectedYearOrMonth >= this.minDate.month;
  }
}
